import { Building2, MapPin, Phone, Mail, Globe, Briefcase, CreditCard, Calendar, FileText, Hash, Shield } from 'lucide-react';
import type { Organization } from '../../types/organization';

interface OrganizationSummaryProps {
  organization: Organization;
}

/**
 * Affiche les informations de l'organisation telles que reçues du backend /auth/me
 * Les champs vides/null affichent "N/A"
 */
export function OrganizationSummary({ organization }: OrganizationSummaryProps) {
  // Helper pour formater les valeurs (N/A si vide/null)
  const formatValue = (value: string | null | undefined): string => {
    return value && value.trim() !== '' ? value : 'N/A';
  };

  // Helper pour formater le secteur d'activité (remplacer underscore par espace et capitaliser)
  const formatIndustry = (industry: string | null | undefined): string => {
    if (!industry) return 'N/A';
    return industry.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Helper pour formater la date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Composant pour une ligne d'information
  const InfoRow = ({ icon: Icon, label, value, isLink = false, href }: { 
    icon: React.ElementType; 
    label: string; 
    value: string; 
    isLink?: boolean;
    href?: string;
  }) => (
    <div className="flex items-center gap-3 py-2">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {isLink && href ? (
          <a 
            href={href.startsWith('http') ? href : `https://${href}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:underline truncate block"
          >
            {value}
          </a>
        ) : (
          <p className={`text-sm font-medium truncate ${value === 'N/A' ? 'text-muted-foreground' : 'text-foreground'}`}>
            {value}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* En-tête avec logo et nom */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 px-6 py-5 border-b border-border">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            {organization.logo ? (
              <img
                src={organization.logo}
                alt="Logo"
                className="h-16 w-16 rounded-xl object-contain bg-white dark:bg-card border border-border shadow-sm"
              />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
            )}
          </div>
          
          {/* Nom et informations principales */}
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-foreground truncate">
              {formatValue(organization.name)}
            </h2>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {organization.legalForm && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {organization.legalForm}
                </span>
              )}
              {organization.industry && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  {formatIndustry(organization.industry)}
                </span>
              )}
              {organization.currency && (
                <span className="text-xs text-muted-foreground">
                  Devise: <span className="font-medium text-foreground">{organization.currency}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="p-6">
        {/* Grille d'informations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
          {/* Colonne gauche - Identifiants légaux */}
          <div className="space-y-1">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Identifiants légaux
            </h3>
            <InfoRow icon={FileText} label="RCCM" value={formatValue(organization.registrationNumber)} />
            <InfoRow icon={Hash} label="Numéro d'Impôt (NIF)" value={formatValue(organization.taxId)} />
            <InfoRow icon={Hash} label="Numéro TVA" value={formatValue(organization.vatNumber)} />
            <InfoRow icon={Shield} label="Mode comptable" value={formatValue(organization.accountingMode)} />
          </div>

          {/* Colonne droite - Affiliations */}
          <div className="space-y-1">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Affiliations sociales
            </h3>
            <InfoRow icon={Shield} label="CNSS" value={formatValue(organization.cnssNumber)} />
            <InfoRow icon={Shield} label="INPP" value={formatValue(organization.inppNumber)} />
            <InfoRow icon={CreditCard} label="Capital social" value={
              organization.capital 
                ? `${parseInt(organization.capital).toLocaleString('fr-FR')} ${organization.currency || 'CDF'}` 
                : 'N/A'
            } />
          </div>
        </div>

        {/* Séparateur */}
        <div className="border-t border-border my-5" />

        {/* Coordonnées */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Coordonnées
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
            <InfoRow 
              icon={MapPin} 
              label="Adresse" 
              value={[organization.address, organization.city, organization.country].filter(Boolean).join(', ') || 'N/A'} 
            />
            <InfoRow icon={Phone} label="Téléphone" value={formatValue(organization.phone)} />
            <InfoRow icon={Mail} label="Email" value={formatValue(organization.email)} />
            <InfoRow 
              icon={Globe} 
              label="Site web" 
              value={formatValue(organization.website)}
              isLink={!!organization.website}
              href={organization.website || undefined}
            />
          </div>
        </div>

        {/* Métadonnées */}
        {(organization.createdAt || organization.updatedAt) && (
          <>
            <div className="border-t border-border my-5" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>Créé le {formatDate(organization.createdAt)}</span>
              </div>
              {organization.updatedAt && organization.updatedAt !== organization.createdAt && (
                <span>Modifié le {formatDate(organization.updatedAt)}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}